import React, { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/utils/currency";

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  currency?: string;
  maxValue?: number; // Max value in cents (e.g., 9999 = $99.99)
  onValueChange?: (value: number | undefined) => void; // Returns value in cents
  showSymbol?: boolean;
  /**
   * Currency values are handled as integers in cents.
   * - value: number in cents (e.g., 1250 = $12.50)
   * - maxValue: maximum value in cents (e.g., 9999 = $99.99)
   * - onValueChange: callback receives value in cents
   */
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      currency,
      maxValue = 999999, // Default max: $9,999.99 (99999 cents)
      onValueChange,
      showSymbol = true,
      className,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const { currentSalon } = useSalonStore();

    // Use provided currency or salon's currency, default to USD
    const currencyCode = currency || currentSalon?.currency || "USD";
    const currencySymbol = getCurrencySymbol(currencyCode);

    // Format cents to dollar display (e.g., 1250 cents -> "12.50")
    const formatCentsToDisplay = (cents: number): string => {
      return (cents / 100).toFixed(2);
    };

    // Parse display value to cents (e.g., "12.50" -> 1250 cents)
    const parseDisplayToCents = (displayValue: string): number | undefined => {
      const numStr = displayValue.replace(/[^\d.-]/g, "");
      const dollars = Number.parseFloat(numStr);
      if (Number.isNaN(dollars)) return undefined;
      return Math.round(dollars * 100); // Convert dollars to cents
    };

    // Get display value from cents value
    const getDisplayValue = (): string => {
      if (value === undefined || value === null || value === "") {
        return "";
      }
      // Handle different value types from InputHTMLAttributes
      let centsValue: number;
      if (typeof value === "string") {
        centsValue = Number.parseInt(value, 10);
      } else if (typeof value === "number") {
        centsValue = value;
      } else {
        // Handle readonly string[] case (shouldn't happen but type-safe)
        return "";
      }
      if (Number.isNaN(centsValue)) return "";
      return formatCentsToDisplay(centsValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const centsValue = parseDisplayToCents(inputValue);

      // Validate max value (maxValue is already in cents)
      if (centsValue !== undefined && centsValue > maxValue) {
        return; // Don't allow values over max
      }

      // Call custom change handler if provided
      if (onValueChange) {
        onValueChange(centsValue);
      }

      // Call original onChange if provided
      if (onChange) {
        // Create synthetic event with the cents value as string
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: centsValue?.toString() || "",
          },
        };
        onChange(syntheticEvent);
      }
    };

    return (
      <div className="relative">
        {showSymbol && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <span className="text-muted-foreground text-sm font-medium">{currencySymbol}</span>
          </div>
        )}
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          placeholder="0.00"
          className={cn(showSymbol && "pl-10", "text-right", className)}
          value={getDisplayValue()}
          onChange={handleInputChange}
          {...props}
        />
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };
